import { ResponsiveTable } from './components/common/ResponsiveTable';
import React, { useEffect, useState } from 'react';
import { FamilyLinkApi, PatientFamilyLink } from './FamilyLinkApi';
import { buildDisplayName, PatientListItem } from './types';
import { API_CONFIG, getApiUrl, getAuthHeaders } from './config/api';

interface Props { patientId: number; token: string; onResponsibleChanged?: (id: number) => void; }

export default function PatientFamilyLinksPanel({ patientId, token, onResponsibleChanged }: Props) {
  const [links, setLinks] = useState<PatientFamilyLink[]>([]);
  const [patients, setPatients] = useState<PatientListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [relatedId, setRelatedId] = useState('');
  const [relationship, setRelationship] = useState('');

  const mapBackendPatient = (p: any): PatientListItem => {
    const np = { firstName: p.firstName || '', secondName: p.secondName || '', firstLastName: p.firstLastName || '', secondLastName: p.secondLastName || '', presentedName: p.presentedName || '' };
    return { id: p.id, ...np, email: p.email, dateOfBirth: p.dateOfBirth, phoneNumber: p.phoneNumber, address: p.address, status: p.status, notes: p.notes, profilePicturePath: p.profilePicturePath, referralSource: p.referralSource, responsiblePartyId: p.responsiblePartyId, name: buildDisplayName(np) };
  };

  const load = () => {
    setLoading(true);
    Promise.all([
      FamilyLinkApi.list(token, patientId),
      fetch(getApiUrl(API_CONFIG.ENDPOINTS.PATIENTS), { headers: getAuthHeaders(token) }).then(r => { if(!r.ok) throw new Error('Patients load failed'); return r.json(); })
    ])
      .then(([l, pats]) => { setLinks(l); setPatients(pats.map(mapBackendPatient)); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  // Reload when patient/token changes; `load` intentionally stays local.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [patientId, token]);

  const handleAdd = async () => {
    if (!relatedId || !relationship) return;
    setError('');
    try { await FamilyLinkApi.add(token, patientId, Number(relatedId), relationship); setRelatedId(''); setRelationship(''); load(); }
    catch (e: any) { setError(e.message); }
  };

  const handleRemove = async (linkId: number) => {
    if (!window.confirm('Remove this link?')) return;
    setError('');
    try { await FamilyLinkApi.remove(token, patientId, linkId); load(); }
    catch (e: any) { setError(e.message); }
  };

  const handleSetResponsible = async (responsibleId: number) => {
    if (!window.confirm('Set this related patient as responsible?')) return;
    setError('');
    try { await FamilyLinkApi.setResponsible(token, patientId, responsibleId); load(); onResponsibleChanged?.(responsibleId); }
    catch (e: any) { setError(e.message); }
  };

  if (loading) return <div>Loading family links...</div>;
  return (
    <div style={{marginTop:32}}>
      <h3>Family Links</h3>
      {error && <div style={{color:'red'}}>{error}</div>}
      <div className="responsive-row" style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center',marginBottom:12}}>
        <select value={relatedId} onChange={e => setRelatedId(e.target.value)} style={{ padding: '6px', border: '1px solid #334155', borderRadius: 4, background: '#0f172a', color: '#f1f5f9' }}>
          <option value=''>Select related patient...</option>
          {patients.filter(p => p.id !== patientId && !links.some(l => l.relatedPatientId === p.id)).map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <input placeholder='Relationship (e.g., Mother)' value={relationship} onChange={e => setRelationship(e.target.value)} style={{ padding: '6px', border: '1px solid #334155', borderRadius: 4, background: '#0f172a', color: '#f1f5f9' }} />
        <button onClick={handleAdd} disabled={!relatedId || !relationship} style={{ padding: '6px 12px', background: '#1976d2', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Add Link</button>
      </div>
      {links.length === 0 ? <div>No family links.</div> : (
        <ResponsiveTable><table style={{borderCollapse:'collapse',width:'100%'}}>
          <thead>
            <tr style={{background:'#1e293b',color:'#f1f5f9'}}>
              <th style={{padding:6, textAlign: 'left'}}>Related Patient</th>
              <th style={{padding:6, textAlign: 'left'}}>Relationship</th>
              <th style={{padding:6, textAlign: 'left'}}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {links.map(l => {
              const related = patients.find(p => p.id === l.relatedPatientId);
              return (
                <tr key={l.id} style={{ borderBottom: '1px solid #334155', color: '#f1f5f9' }}>
                  <td style={{padding:6}}>{related?.name || l.relatedPatientId}</td>
                  <td style={{padding:6}}>{l.relationship}</td>
                  <td className="responsive-row" style={{padding:6,display:'flex',gap:8}}>
                    <button onClick={() => handleRemove(l.id)} style={{ padding: '4px 8px', background: '#ef4444', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Remove</button>
                    <button onClick={() => handleSetResponsible(l.relatedPatientId)} style={{ padding: '4px 8px', background: '#10b981', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Set Responsible</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table></ResponsiveTable>
      )}
    </div>
  );
}
